import { useEffect, useState } from "react";

import { api } from "../api/client";
import type { ApprovalDetail } from "../api/client";
import { describeApproval } from "./describeApproval";

type Decision = "approve" | "reject";

type DecisionBody = {
  decision: Decision;
  content_hash: string;
  justification?: string;
  self_approval_acknowledged: boolean;
};

type ApprovalDecisionProps = {
  projectId: string;
  approvalId: string;
  currentUserId: string;
  canDecideMedium: boolean;
  canDecideHigh: boolean;
  canDecideOwnerOnly?: boolean;
  api?: {
    getApproval(projectId: string, id: string): Promise<ApprovalDetail>;
    decide(projectId: string, id: string, body: DecisionBody): Promise<unknown>;
  };
  onDecided?: (decision: Decision, info?: { apply?: string }) => void;
  onLoaded?: (detail: ApprovalDetail) => void;
};

const OWNER_ONLY_ACTIONS = new Set([
  "budget.limit_increase",
  "budget.override_limit",
  "budget.override_unbounded_cost",
]);

function resolvePermitted(
  actionType: string,
  risk: string,
  rights: { canDecideMedium: boolean; canDecideHigh: boolean; canDecideOwnerOnly: boolean },
): boolean {
  if (OWNER_ONLY_ACTIONS.has(actionType)) {
    return rights.canDecideOwnerOnly;
  }
  if (risk === "medium") {
    return rights.canDecideMedium;
  }
  return rights.canDecideHigh;
}

function notDecidableText(
  alreadyDecided: boolean,
  permitted: boolean,
  status: string,
  ownerOnly: boolean,
): string {
  if (alreadyDecided) {
    return "Već ste odlučili o ovom odobrenju.";
  }
  if (permitted) {
    return `Ovo odobrenje nije u stanju koje dopušta odluku (${status}).`;
  }
  if (ownerOnly) {
    return "Ovo odobrenje može donijeti samo vlasnik organizacije.";
  }
  return "Nemate ovlast za odluku na ovoj razini rizika.";
}

export function ApprovalDecision({
  projectId,
  approvalId,
  currentUserId,
  canDecideMedium,
  canDecideHigh,
  canDecideOwnerOnly = false,
  api: apiClient = api,
  onDecided,
  onLoaded,
}: ApprovalDecisionProps) {
  const [detail, setDetail] = useState<ApprovalDetail | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [justification, setJustification] = useState("");
  const [error, setError] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setDetail(null);
    setAcknowledged(false);
    setJustification("");
    setError(false);
    setSaveError(false);

    apiClient
      .getApproval(projectId, approvalId)
      .then((loaded) => {
        if (!active) {
          return;
        }
        setDetail(loaded);
        setAcknowledged(false);
        setJustification("");
        setError(false);
        onLoaded?.(loaded);
      })
      .catch(() => {
        if (active) {
          setError(true);
        }
      });

    return () => {
      active = false;
    };
  }, [projectId, approvalId]);

  if (error && !detail) {
    return (
      <p data-testid="approval-decision-error" className="text-sm text-destructive">
        Odluku nije moguće učitati.
      </p>
    );
  }

  if (!detail) {
    return <p className="text-sm text-muted-foreground">Učitavanje…</p>;
  }

  const ownRequest = detail.requested_by === currentUserId;
  const ownerOnly = OWNER_ONLY_ACTIONS.has(detail.action_type);
  const permitted = resolvePermitted(detail.action_type, detail.risk, {
    canDecideMedium,
    canDecideHigh,
    canDecideOwnerOnly,
  });
  const alreadyDecided = detail.decisions.some((row) => row.user_id === currentUserId);
  // Self-approval is only legal for medium risk and only with acknowledgement.
  const selfApprovalBlocked = ownRequest && (detail.risk !== "medium" || !acknowledged);

  const decide = async (decision: Decision) => {
    setBusy(true);
    setSaveError(false);
    try {
      const result = await apiClient.decide(projectId, detail.id, {
        decision,
        content_hash: detail.content_hash,
        justification: justification || undefined,
        self_approval_acknowledged: acknowledged,
      });
      const apply = (result as { apply?: string } | null | undefined)?.apply;
      onDecided?.(decision, { apply });
    } catch {
      setSaveError(true);
    } finally {
      setBusy(false);
    }
  };

  const description = describeApproval(detail);

  return (
    <section data-testid="approval-detail" className="space-y-3">
      <div data-testid="approval-describe">
        <p className="font-medium">{description.title}</p>
        <ul className="mt-1 list-disc pl-5 text-sm">
          {description.lines.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      </div>

      <details data-testid="approval-technical" className="text-xs">
        <summary>Tehnički detalji</summary>
        <div className="mt-1 space-y-2">
          <p>{detail.id}</p>
          <p>
            {detail.action_type} · rizik {detail.risk} · status {detail.status}
          </p>
          <p>{detail.content_hash}</p>
          <p className="font-medium">Predložena promjena (diff)</p>
          <pre className="overflow-auto">{JSON.stringify(detail.diff, null, 2)}</pre>
          <p className="font-medium">Cilj</p>
          <pre className="overflow-auto">{JSON.stringify(detail.target_ref, null, 2)}</pre>
          <ul className="list-disc pl-5">
            {detail.decisions.map((row) => (
              <li key={`${row.user_id}-${row.decided_at}`}>
                {row.user_id}: {row.decision}
                {row.self_approval && " (samoodobrenje)"}
              </li>
            ))}
          </ul>
        </div>
      </details>

      {saveError ? (
        <p data-testid="approval-decision-error" className="text-sm text-destructive">
          Odluka nije spremljena.
        </p>
      ) : null}

      {detail.status === "proposed" && permitted && !alreadyDecided ? (
        <div className="space-y-2">
          {ownRequest && (
            <label className="flex items-start gap-2 text-sm" data-testid="approval-self-ack">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
              />
              <span>
                Potvrđujem da sam svjestan da odobravam vlastiti zahtjev. To se trajno bilježi u
                auditu.
              </span>
            </label>
          )}

          <label className="block text-sm">
            <span className="text-muted-foreground">Obrazloženje (nije obavezno)</span>
            <input
              value={justification}
              onChange={(event) => setJustification(event.target.value)}
              className="mt-1 min-h-11 w-full rounded border border-border bg-background px-3 py-2"
            />
          </label>

          <div className="flex gap-2">
            <button
              type="button"
              data-testid="approval-approve"
              className="min-h-11 rounded bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
              disabled={busy || selfApprovalBlocked}
              onClick={() => void decide("approve")}
            >
              Odobri
            </button>
            <button
              type="button"
              data-testid="approval-reject"
              className="min-h-11 rounded border border-border px-4 py-2 text-sm disabled:opacity-50"
              disabled={busy}
              onClick={() => void decide("reject")}
            >
              Odbij
            </button>
          </div>

          {selfApprovalBlocked && (
            <p className="text-xs text-warn">
              Samoodobrenje je dopušteno samo za srednji rizik i uz izričitu potvrdu.
            </p>
          )}
        </div>
      ) : (
        <p data-testid="approval-not-decidable" className="text-xs text-muted-foreground">
          {notDecidableText(alreadyDecided, permitted, detail.status, ownerOnly)}
        </p>
      )}
    </section>
  );
}
